/**
 * CommsFeedReport — the live communications board (Reports → Live feed, ADMIN-ONLY).
 * A running, newest-first list of every patient communication across channels:
 * texts (SMS), emails, voice calls (with the transcript or a synthesized
 * disposition), and faxes. Each row shows the patient's name + pipeline status,
 * who handled it, and what was said — filterable by staff user, channel, and
 * direction (study + time range come from the shared Reports toolbar).
 *
 * It self-refreshes every 12s (the audit ledger is spared the silent polls via
 * ?audit=0 — only the deliberate open / filter change / "load older" is audited
 * server-side). Polls merge into place by id (so a status flip updates a row and
 * any older pages you loaded stay put); "Load older" pages back via ?before.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare, Mail, Phone, Printer, ArrowDownLeft, ArrowUpRight, Radio, RefreshCw, Voicemail, Info,
} from 'lucide-react';
import api from '../../../services/api';
import { CommFeedChannel, CommFeedResponse, CommFeedRow } from '../../types';
import { relTime, dateTime } from '../../lib';
import LifecycleBadge from '../LifecycleBadge';
import { FilterBar, FilterSelect } from '../filters';
import { fmtDuration } from './StatBlocks';

interface CommsFeedReportProps {
  from: string | null;
  to: string | null;
  /** Scope to one study (by each lead's current primary study). */
  studyId?: string | null;
}

const PAGE = 60;

const CHANNEL_OPTIONS = [
  { value: 'all', label: 'All channels' },
  { value: 'sms', label: 'Texts' },
  { value: 'email', label: 'Emails' },
  { value: 'call', label: 'Calls' },
  { value: 'fax', label: 'Faxes' },
];

const DIRECTION_OPTIONS = [
  { value: 'all', label: 'All directions' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'inbound', label: 'Inbound' },
];

/** Per-channel icon + accent (CRM cyan for texts; semantic hues elsewhere). */
function channelMeta(channel: CommFeedChannel): { Icon: typeof MessageSquare; ring: string; label: string } {
  switch (channel) {
    case 'sms': return { Icon: MessageSquare, ring: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300', label: 'Text' };
    case 'email': return { Icon: Mail, ring: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300', label: 'Email' };
    case 'call': return { Icon: Phone, ring: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', label: 'Call' };
    case 'fax': return { Icon: Printer, ring: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', label: 'Fax' };
    default: return { Icon: MessageSquare, ring: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300', label: channel };
  }
}

/** Delivery-status pill colour. Green = landed, rose = problem, else neutral. */
function statusTone(status: string | null): string {
  const s = (status || '').toLowerCase();
  if (['delivered', 'completed', 'signed', 'received', 'matched', 'sent'].includes(s))
    return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  if (['failed', 'undelivered', 'no-answer', 'busy', 'bounced', 'complained', 'suppressed', 'discarded', 'canceled', 'expired'].includes(s))
    return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
  return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
}

/** Friendly labels for raw provider status codes (falls back to the raw value). */
const STATUS_LABELS: Record<string, string> = {
  'no-answer': 'No answer', undelivered: 'Undelivered', bounced: 'Bounced',
  complained: 'Complaint', suppressed: 'Suppressed', canceled: 'Canceled',
  failed: 'Failed', busy: 'Busy', delivered: 'Delivered', completed: 'Completed',
  sent: 'Sent', queued: 'Queued', sending: 'Sending', received: 'Received',
  matched: 'Matched', needs_match: 'Needs match', discarded: 'Discarded',
  signed: 'Signed', expired: 'Expired', ringing: 'Ringing', initiated: 'Calling',
};
function statusLabel(status: string | null): string {
  if (!status) return '';
  return STATUS_LABELS[status.toLowerCase()] || status;
}

/** Merge fresh rows over existing ones by id, newest-first. Fresh wins (status flips). */
function mergeRows(incoming: CommFeedRow[], existing: CommFeedRow[]): CommFeedRow[] {
  const byId = new Map<string, CommFeedRow>();
  for (const r of existing) byId.set(r.id, r);
  for (const r of incoming) byId.set(r.id, r);
  return [...byId.values()].sort((a, b) =>
    a.occurred_at < b.occurred_at ? 1 : a.occurred_at > b.occurred_at ? -1 : 0,
  );
}

export default function CommsFeedReport({ from, to, studyId = null }: CommsFeedReportProps) {
  const [rows, setRows] = useState<CommFeedRow[]>([]);
  const [team, setTeam] = useState<{ id: string; name: string }[]>([]);
  const [userId, setUserId] = useState('');
  const [channel, setChannel] = useState('all');
  const [direction, setDirection] = useState('all');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  // Keep prior rows on screen during the silent 12s background polls.
  const firstLoad = useRef(true);
  // Guards the (un-aborted) "Load older" request from setState-after-unmount.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const paramsFor = useCallback(
    (extra: Record<string, string | number | undefined>) => ({
      from: from || undefined,
      to: to || undefined,
      study_id: studyId || undefined,
      user_id: userId || undefined,
      channel: channel === 'all' ? undefined : channel,
      direction: direction === 'all' ? undefined : direction,
      limit: PAGE,
      ...extra,
    }),
    [from, to, studyId, userId, channel, direction],
  );

  // Fetch on mount + whenever a filter changes, then poll every 12s. The alive
  // flag + interval cleanup stop a stale filter's response from landing.
  useEffect(() => {
    let alive = true;
    firstLoad.current = true;
    setLoading(true);
    // Deliberate load (mount / filter change) is audited; polls pass audit=0.
    const load = (audited: boolean) => {
      api
        .get<CommFeedResponse>('/api/crm/analytics/communications', {
          params: paramsFor(audited ? {} : { audit: 0 }),
        })
        .then((res) => {
          if (!alive) return;
          const data = res.data;
          setTeam(data.team || []);
          setRows((prev) => (firstLoad.current ? data.rows || [] : mergeRows(data.rows || [], prev)));
          // Only the deliberate head fetch establishes hasMore; polls of the head
          // would always report "more older exist" and clobber the load-older cursor.
          if (firstLoad.current) setHasMore(!!data.has_more);
          setError(null);
        })
        .catch((err) => {
          if (!alive) return;
          if (firstLoad.current) setError(err?.response?.data?.error || 'Failed to load communications');
        })
        .finally(() => {
          if (!alive) return;
          firstLoad.current = false;
          setLoading(false);
        });
    };
    load(true);
    const poll = setInterval(() => load(false), 12000);
    return () => {
      alive = false;
      clearInterval(poll);
    };
  }, [paramsFor]);

  const loadOlder = useCallback(() => {
    const oldest = rows[rows.length - 1]?.occurred_at;
    if (!oldest || loadingMore) return;
    setLoadingMore(true);
    api
      .get<CommFeedResponse>('/api/crm/analytics/communications', { params: paramsFor({ before: oldest }) })
      .then((res) => {
        if (!mounted.current) return;
        const merged = mergeRows(res.data.rows || [], rows);
        setRows(merged);
        // If the page brought nothing new, we've reached the end — also stops the
        // .lte boundary cursor from looping on a cluster of tied timestamps.
        setHasMore(merged.length > rows.length && !!res.data.has_more);
      })
      .catch(() => { /* keep what's on screen; the next poll recovers */ })
      .finally(() => { if (mounted.current) setLoadingMore(false); });
  }, [rows, loadingMore, paramsFor]);

  const userOptions = useMemo(
    () => [
      { value: '', label: 'All users' },
      ...team.map((t) => ({ value: t.id, label: t.name })),
    ],
    [team],
  );
  const selectedUserName = useMemo(() => team.find((t) => t.id === userId)?.name || '', [team, userId]);

  return (
    <div className="space-y-4">
      {/* Live-feed sub-toolbar: user / channel / direction (study + range above). */}
      <FilterBar>
        <FilterSelect value={userId} onChange={setUserId} options={userOptions} auto label="Sent by" title="Show only the messages one staff member sent" />
        <FilterSelect value={channel} onChange={setChannel} options={CHANNEL_OPTIONS} auto label="Channel" />
        <FilterSelect value={direction} onChange={setDirection} options={DIRECTION_OPTIONS} auto label="Direction" />
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-500 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
          </span>
          <Radio className="h-3.5 w-3.5" /> Live · refreshes every 12s
        </span>
      </FilterBar>

      {userId && (
        <p className="flex items-start gap-1.5 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            Showing messages <span className="font-medium">sent by {selectedUserName || 'this user'}</span>. Inbound
            replies and received faxes aren&rsquo;t attributed to a sender, so they&rsquo;re hidden while a
            &ldquo;Sent by&rdquo; filter is active.
          </span>
        </p>
      )}

      {loading && rows.length === 0 ? (
        <p className="p-4 text-sm text-gray-400">Loading communications…</p>
      ) : error && rows.length === 0 ? (
        <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <MessageSquare className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No communications in this range</p>
          <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
            Texts, emails, calls, and faxes will appear here as they happen.
          </p>
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {rows.map((r) => {
              const { Icon, ring, label } = channelMeta(r.channel);
              const inbound = r.direction === 'inbound';
              const DirIcon = inbound ? ArrowDownLeft : ArrowUpRight;
              const actor = inbound ? null : r.user_name || 'Automated';
              return (
                <li
                  key={r.id}
                  className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 sm:p-4"
                >
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${ring}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {r.contact_id ? (
                          <Link
                            to={`/crm/contacts/${r.contact_id}`}
                            title={`Open ${r.contact_name}'s profile`}
                            className="truncate text-sm font-semibold text-gray-900 hover:text-primary-700 hover:underline focus:outline-none focus-visible:text-primary-700 focus-visible:underline dark:text-gray-100 dark:hover:text-primary-400 dark:focus-visible:text-primary-400"
                          >
                            {r.contact_name}
                          </Link>
                        ) : (
                          <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {r.contact_name}
                          </span>
                        )}
                        {r.contact_status && <LifecycleBadge status={r.contact_status} />}
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          <DirIcon className="h-3 w-3" />
                          {inbound ? 'In' : 'Out'}
                        </span>
                        {r.voicemail && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            <Voicemail className="h-3 w-3" /> Voicemail
                          </span>
                        )}
                        {r.status && (
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusTone(r.status)}`}>
                            {statusLabel(r.status)}
                          </span>
                        )}
                      </div>

                      {r.subject && (
                        <p className="mt-1 truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                          {r.subject}
                        </p>
                      )}
                      <p className="mt-0.5 whitespace-pre-wrap break-words text-sm leading-snug text-gray-600 line-clamp-3 dark:text-gray-300">
                        {r.preview || '—'}
                      </p>

                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-400 dark:text-gray-500">
                        <span>{label}</span>
                        {actor && <span>· {actor}</span>}
                        {r.duration_sec ? <span>· {fmtDuration(r.duration_sec)}</span> : null}
                        <span title={dateTime(r.occurred_at)}>· {relTime(r.occurred_at)}</span>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {hasMore && (
            <div className="flex justify-center pt-1">
              <button
                type="button"
                onClick={loadOlder}
                disabled={loadingMore}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-900 sm:min-h-0"
              >
                {loadingMore && <RefreshCw className="h-4 w-4 animate-spin" />}
                {loadingMore ? 'Loading…' : 'Load older'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
